import AllAlert from '@rsos/components/capstone/common/Map/assets/allalerts.svg';
import AllAlertActiveAssailant from '@rsos/components/capstone/common/Map/assets/allalerts_active_assailant.svg';
import AllAlertActiveAssailantHover from '@rsos/components/capstone/common/Map/assets/allalerts_active_assailant_hover.svg';
import AllAlertHover from '@rsos/components/capstone/common/Map/assets/allalerts_hover.svg';
import AllAlertTest from '@rsos/components/capstone/common/Map/assets/allalerts_test.svg';
import AllAlertTestHover from '@rsos/components/capstone/common/Map/assets/allalerts_test_hover.svg';
import AllAlertTrain from '@rsos/components/capstone/common/Map/assets/allalerts_train.svg';
import AllAlertTrainHover from '@rsos/components/capstone/common/Map/assets/allalerts_train_hover.svg';
import AllAlertUnread from '@rsos/components/capstone/common/Map/assets/allalerts_unread.svg';
import AllAlertUnreadHover from '@rsos/components/capstone/common/Map/assets/allalerts_unread_hover.svg';
import AllCallMarker from '@rsos/components/capstone/common/Map/assets/allcall_marker.svg';
import AllCallMarkerHover from '@rsos/components/capstone/common/Map/assets/allcall_marker_hover.svg';
import AllCallSMS from '@rsos/components/capstone/common/Map/assets/allcall_textto911.svg';
import AllCallSMSHover from '@rsos/components/capstone/common/Map/assets/allcall_textto911_hover.svg';
import CallLandline from '@rsos/components/capstone/common/Map/assets/call_landline.svg';
import CallSelected from '@rsos/components/capstone/common/Map/assets/call_selected.svg';
import CallSelectedHover from '@rsos/components/capstone/common/Map/assets/call_selected_hover.svg';
import CallVOIP from '@rsos/components/capstone/common/Map/assets/call_voip.svg';
import CallStatusAnswered from '@rsos/components/capstone/common/Map/assets/calls/call_status_answered.svg';
import CallStatusAnsweredHover from '@rsos/components/capstone/common/Map/assets/calls/call_status_answered_hover.svg';
import CallStatusOnHold from '@rsos/components/capstone/common/Map/assets/calls/call_status_on_hold.svg';
import CallStatusOnHoldHover from '@rsos/components/capstone/common/Map/assets/calls/call_status_on_hold_hover.svg';
import CallStatusReleased from '@rsos/components/capstone/common/Map/assets/calls/call_status_released.svg';
import CallStatusReleasedHover from '@rsos/components/capstone/common/Map/assets/calls/call_status_released_hover.svg';
import CallStatusRinging from '@rsos/components/capstone/common/Map/assets/calls/call_status_ringing.svg';
import CallStatusRingingHover from '@rsos/components/capstone/common/Map/assets/calls/call_status_ringing_hover.svg';
import SMSSelected from '@rsos/components/capstone/common/Map/assets/sms_selected.svg';
import { clearInstanceListeners } from '@rsos/components/capstone/common/Map/utils';
import {
  BE_CALL_ALERTING,
  BE_CALL_ANSWERED,
  BE_HOLD_CALL,
  BE_END_CALL,
  BE_RETRIEVE_CALL,
} from '@rsos/constants/callStatuses';
import { ALERTS } from '@rsos/constants/incidentTypes';
import { LANDLINE, VOIP, TEXT, OTHER } from '@rsos/constants/locationTypes';
import type { AlertLocationGeodetic } from '@rsos/reduxDataStore/src/features/alerts/alerts.types';
import type {
  EmergencyCallLocation,
  EmergencyCallSecondaryLocation,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCalls.types';
import getMostRecentCallStatus from '@rsos/utils/getMostRecentCallStatus';
import isValidCoordinates from '@rsos/utils/isValidCoordinates';
import { isIgnoreType } from '../../JurisdictionView/Map/markers/calls/ignoreUtils';

type Incident =
  | EmergencyCallLocation
  | EmergencyCallSecondaryLocation
  | AlertLocationGeodetic;
type RapidMarker = google.maps.Marker & { rsosID?: string };
type RapidInfoWindow = google.maps.InfoWindow & {
  rsosID?: string;
  shouldShowWindow?: boolean;
  setMap?: (map: google.maps.Map | null) => void;
};

export const clearMarker = (marker: RapidMarker | null | undefined) => {
  if (marker) {
    marker.setMap(null);
    clearInstanceListeners(marker);
  }
};

export const clearInfoWindow = (
  infoWindow: RapidInfoWindow | null | undefined,
) => {
  if (infoWindow) {
    infoWindow.setMap?.(null);
    clearInstanceListeners(infoWindow);
  }
};

interface ShouldShowMarkerParams {
  incident: Incident;
  isAllView: boolean;
  incidentIdx: number;
  incidentType: string;
  mostRecentIncidentIdx?: number;
}

// From Google's docs:
// Renders the Marker on the specified map or panorama. If map is set to null, the marker will be removed.
export const shouldShowMarker = ({
  incident,
  isAllView,
  incidentIdx,
  incidentType,
  mostRecentIncidentIdx,
}: ShouldShowMarkerParams): boolean => {
  const hasLocation = isValidCoordinates(incident);
  const isIgnored = isIgnoreType(
    incident as unknown as Parameters<typeof isIgnoreType>[0],
  );
  const isMostRecentIdx =
    mostRecentIncidentIdx !== undefined &&
    mostRecentIncidentIdx === incidentIdx;
  const isHistoricVisible =
    !isAllView &&
    (incidentType === ALERTS
      ? incidentIdx > (mostRecentIncidentIdx ?? -1)
      : incidentIdx < (mostRecentIncidentIdx ?? Infinity));

  return (
    hasLocation &&
    !isIgnored &&
    (isAllView || isMostRecentIdx || isHistoricVisible)
  );
};

export const revertMarkerIcon = (
  marker: RapidMarker,
  hasCallStatus: boolean,
) => {
  const markerIcon = marker.getIcon();

  // alerts
  if (markerIcon === AllAlertUnreadHover) {
    marker.setIcon(AllAlertUnread);
  } else if (markerIcon === AllAlertHover) {
    marker.setIcon(AllAlert);
  } else if (markerIcon === AllAlertActiveAssailantHover) {
    marker.setIcon(AllAlertActiveAssailant);
  } else if (markerIcon === AllAlertTrainHover) {
    marker.setIcon(AllAlertTrain);
  } else if (markerIcon === AllAlertTestHover) {
    marker.setIcon(AllAlertTest);
  }
  // sms & calls
  else if (markerIcon === AllCallSMSHover) {
    marker.setIcon(AllCallSMS);
  } else if (markerIcon === AllCallMarkerHover) {
    marker.setIcon(AllCallMarker);
  } else if (hasCallStatus && markerIcon === CallStatusAnsweredHover) {
    marker.setIcon(CallStatusAnswered);
  } else if (hasCallStatus && markerIcon === CallStatusOnHoldHover) {
    marker.setIcon(CallStatusOnHold);
  } else if (hasCallStatus && markerIcon === CallStatusReleasedHover) {
    marker.setIcon(CallStatusReleased);
  } else if (hasCallStatus && markerIcon === CallStatusRingingHover) {
    marker.setIcon(CallStatusRinging);
  }
};

interface SetCallStatusMarkerIconParams {
  marker: RapidMarker;
  filteredCalls: EmergencyCallLocation[] | EmergencyCallSecondaryLocation[];
  hoveredID: string | null;
  hasCallStatus: boolean;
}

export const setCallStatusMarkerIcon = ({
  marker,
  filteredCalls,
  hoveredID,
  hasCallStatus,
}: SetCallStatusMarkerIconParams) => {
  const callStatus = getMostRecentCallStatus({
    callerID: marker.rsosID,
    callQueue: filteredCalls,
    hasAniAliAccess: hasCallStatus,
    hasCallStatusAccess: hasCallStatus,
  });

  if (hoveredID) {
    if (hasCallStatus && callStatus) {
      switch (callStatus) {
        case BE_CALL_ANSWERED:
        case BE_RETRIEVE_CALL:
          marker.setIcon(CallStatusAnsweredHover);
          break;
        case BE_HOLD_CALL:
          marker.setIcon(CallStatusOnHoldHover);
          break;
        case BE_END_CALL:
          marker.setIcon(CallStatusReleasedHover);
          break;
        case BE_CALL_ALERTING:
          marker.setIcon(CallStatusRingingHover);
          break;
      }
    } else {
      marker.setIcon(AllCallMarkerHover);
    }
  } else {
    if (hasCallStatus && callStatus) {
      switch (callStatus) {
        case BE_CALL_ANSWERED:
        case BE_RETRIEVE_CALL:
          marker.setIcon(CallStatusAnswered);
          break;
        case BE_HOLD_CALL:
          marker.setIcon(CallStatusOnHold);
          break;
        case BE_END_CALL:
          marker.setIcon(CallStatusReleased);
          break;
        case BE_CALL_ALERTING:
          marker.setIcon(CallStatusRinging);
          break;
      }
    } else {
      marker.setIcon(AllCallMarker);
    }
  }
};

export const setAniAliMarkerIcon = (
  marker: RapidMarker,
  incident: Incident,
  isHovered: boolean,
) => {
  const classOfServiceCategory =
    'class_of_service_category' in incident
      ? incident.class_of_service_category
      : null;
  if (classOfServiceCategory === TEXT) {
    marker.setIcon(SMSSelected);
  } else if (classOfServiceCategory === VOIP) {
    marker.setIcon(CallVOIP);
  } else if (
    classOfServiceCategory === LANDLINE ||
    classOfServiceCategory === OTHER
  ) {
    marker.setIcon(CallLandline);
  } else {
    if (isHovered) {
      marker.setIcon(CallSelectedHover);
    } else {
      marker.setIcon(CallSelected);
    }
  }
};
